import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Droplets, Sun, Sparkles, Wrench, EyeOff, ScanLine } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api, track } from "@/lib/api";
import { IMG } from "@/constants/images";

const full = (url) => (url && url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

const benefits = [
  [EyeOff, "Invisible Protection"], [Sparkles, "Stain Resistant"], [Droplets, "Water Repellent"],
  [Sun, "UV Protection"], [Wrench, "Easy Maintenance"], [ShieldCheck, "Professional Application"],
];

const projects = [
  ["Luxury Villa", "New Delhi", IMG.proj1], ["Penthouse Residence", "Gurugram", IMG.proj4],
  ["5 Star Hotel", "Mumbai", IMG.proj3], ["Corporate Office", "Bangalore", IMG.proj2],
];

export default function Home() {
  const navigate = useNavigate();
  const [c, setC] = useState({});
  const [projectList, setProjectList] = useState(projects);
  const [surfaces, setSurfaces] = useState([]);
  useEffect(() => {
    track("page_view", { page: "home" });
    api.get("/content/homepage").then(({ data }) => data && Object.keys(data).length && setC(data)).catch(() => {});
    api.get("/projects").then(({ data }) => data.length && setProjectList(data.map((p) => [p.title, p.location, full(p.image) || IMG.proj1]))).catch(() => {});
    api.get("/surface-guide").then(({ data }) => setSurfaces(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#080808]">
      <Seo title="Surface Protection for Modern Living" description="ERRAH — Premium Surface Protection Films (SPF) and Nano Coatings for homes, furniture, interiors and commercial spaces. Protect what matters." />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={c.hero_image ? full(c.hero_image) : IMG.hero} alt="Luxury dining interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="errah-label mb-5">
            {c.hero_eyebrow || "Surface Protection for Modern Living"}
          </motion.p>
          {c.hero_title ? (
            <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="serif-head text-5xl sm:text-6xl lg:text-8xl max-w-4xl">
              {c.hero_title}
            </motion.h1>
          ) : (
            <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="serif-head text-5xl sm:text-6xl lg:text-8xl max-w-4xl">
              PROTECT EVERY<br />SURFACE <span className="gold-text italic">THAT MATTERS.</span>
            </motion.h1>
          )}
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
            className="mt-6 text-base lg:text-lg text-gray-300 max-w-xl font-light leading-relaxed">
            {c.hero_description || "AI-powered surface protection for homes, offices, restaurants and hotels. Show us your surface — ERRAH recommends how to protect it."}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.35 }}
            className="mt-9 flex flex-col sm:flex-row gap-3">
            <button onClick={() => navigate("/ai-scan")} data-testid="hero-scan-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#E4C987] hover:-translate-y-0.5 transition-all rounded-sm">
              <ScanLine size={17} /> SCAN YOUR SURFACE
            </button>
            <Link to="/protection" data-testid="hero-explore-cta"
              className="border border-[#C9A45C]/50 px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#C9A45C] hover:text-black transition-colors rounded-sm text-center">
              EXPLORE PROTECTION
            </Link>
            <Link to="/quote" data-testid="hero-quote-cta"
              className="px-8 py-4 text-sm font-semibold tracking-wide text-gray-300 hover:text-white transition-colors rounded-sm text-center underline underline-offset-4 decoration-[#C9A45C]/40">
              REQUEST A QUOTE
            </Link>
          </motion.div>
        </div>
      </section>

      {/* BENEFIT STRIP */}
      <section className="bg-warmblack border-y border-[#C9A45C]/15">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-5 flex gap-8 overflow-x-auto lg:justify-between">
          {benefits.map(([Icon, label]) => (
            <div key={label} className="flex items-center gap-2.5 shrink-0">
              <Icon size={18} className="text-[#C9A45C]" />
              <span className="text-[13px] text-gray-300 whitespace-nowrap">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* EVERY SURFACE. ONE ERRAH. */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16">
        <Reveal>
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="errah-label gold-text mb-3">Every surface. One ERRAH.</p>
              <h2 className="serif-head text-3xl lg:text-5xl max-w-2xl">From marble to velvet — protection for every surface.</h2>
            </div>
            <Link to="/protection" className="hidden sm:inline-flex items-center gap-1 text-xs tracking-widest text-gray-400 hover:text-[#C9A45C] transition-colors">EXPLORE ALL <ArrowRight size={14} /></Link>
          </div>
        </Reveal>
        <div className="flex lg:grid lg:grid-cols-5 gap-3.5 overflow-x-auto lg:overflow-visible snap-x snap-mandatory no-scrollbar -mx-5 px-5 lg:mx-0 lg:px-0" data-testid="home-surface-grid">
          {(surfaces.length ? surfaces : []).slice(0, 10).map((f, i) => (
            <Reveal key={f.slug} delay={(i % 5) * 0.05} className="min-w-[46%] sm:min-w-[30%] lg:min-w-0 snap-start">
              <Link to={`/protection/${f.slug}`} data-testid={`home-surface-${f.slug}`} className="group block hover-zoom relative aspect-[3/4] overflow-hidden hair-border">
                <img src={f.image} alt={f.title} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-4 flex items-center justify-between">
                  <h3 className="font-serif text-base lg:text-lg">{f.title}</h3>
                  <ArrowUpRight size={16} className="text-[#C9A45C] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AI SCAN CTA BAND */}
      <section className="border-y border-[#C9A45C]/20 bg-warmblack">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-14 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <Reveal>
            <p className="errah-label gold-text mb-3">AI Surface Scan</p>
            <h2 className="serif-head text-3xl lg:text-5xl max-w-2xl">Not sure what your surface needs? Let ERRAH AI find out.</h2>
            <p className="mt-4 text-gray-400 max-w-2xl leading-relaxed">
              Upload a photo. ERRAH AI identifies the likely material and condition, recommends the appropriate protection and gives you an estimated investment — in seconds.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <button onClick={() => navigate("/ai-scan")} data-testid="home-scan-band-cta"
              className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#E4C987] hover:-translate-y-0.5 transition-all rounded-sm">
              <ScanLine size={17} /> START AI SURFACE SCAN
            </button>
          </Reveal>
        </div>
      </section>

      {/* CORE SOLUTIONS */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-4">
        <div className="grid md:grid-cols-2 gap-3.5">
          {[
            { tag: "SPF", title: "Surface Protection Film", desc: "Invisible protection for valuable surfaces — dining tables, countertops, furniture, marble & stone.", to: "/solutions/surface-protection-film", cta: "EXPLORE SPF", img: IMG.marble },
            { tag: "NANO", title: "Nano Surface Protection", desc: "Advanced nano protection for fabrics, sofas, curtains, rugs, leather, furniture & glass.", to: "/solutions/nano-coating", cta: "EXPLORE NANO", img: IMG.velvetSofa },
          ].map((s, i) => (
            <Reveal key={s.tag} delay={i * 0.1}>
              <Link to={s.to} className="group relative block aspect-[16/10] overflow-hidden hover-zoom hair-border">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
                <div className="absolute inset-0 p-7 lg:p-9 flex flex-col justify-end">
                  <span className="errah-label mb-2">{s.tag}</span>
                  <h3 className="serif-head text-3xl lg:text-4xl">{s.title}</h3>
                  <p className="mt-2 text-sm text-gray-300 max-w-md leading-relaxed">{s.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 bg-[#C9A45C] text-black w-max px-5 py-2.5 text-xs font-semibold tracking-wide group-hover:bg-[#E4C987] transition-colors">{s.cta} <ArrowRight size={14} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section id="projects" className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16">
        <Reveal><div className="flex items-end justify-between mb-8">
          <h2 className="serif-head text-3xl lg:text-5xl">Featured projects</h2>
          <Link to="/projects" className="inline-flex items-center gap-1 text-xs tracking-widest text-gray-400 hover:text-[#C9A45C] transition-colors">VIEW ALL <ArrowRight size={14} /></Link>
        </div></Reveal>
        <div className="flex lg:grid lg:grid-cols-4 gap-3.5 overflow-x-auto lg:overflow-visible snap-x snap-mandatory no-scrollbar -mx-5 px-5 lg:mx-0 lg:px-0">
          {projectList.map(([name, loc, img], i) => (
            <Reveal key={name} delay={(i % 4) * 0.06} className="min-w-[62%] sm:min-w-[40%] lg:min-w-0 snap-start">
              <div className="group hover-zoom relative aspect-[3/4] overflow-hidden hair-border">
                <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 p-4">
                  <h3 className="font-serif text-lg leading-tight">{name}</h3>
                  <p className="text-xs text-[#C9A45C] tracking-wide">{loc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONSULTATION CTA */}
      <section className="relative overflow-hidden">
        <img src={IMG.livingCTA} alt="Luxury living room" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10 py-20 grid lg:grid-cols-2 gap-8 items-center">
          <Reveal>
            <h2 className="serif-head text-4xl lg:text-6xl">Because life happens.<br /><span className="gold-text italic">We protect.</span></h2>
            <p className="mt-4 text-gray-300 max-w-md">Our experts help you choose the right protection for your space. Book a free consultation today.</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:justify-self-end">
            <div className="bg-[#C9A45C] text-black p-8 max-w-sm">
              <p className="errah-label !text-black/60 mb-2">Free & no obligation</p>
              <h3 className="serif-head text-3xl">Book a Consultation</h3>
              <p className="mt-2 text-sm text-black/80">Tell us what you want to protect and we'll recommend the right ERRAH solution.</p>
              <button onClick={() => navigate("/consultation")} data-testid="cta-consult"
                className="mt-6 w-full bg-black text-white py-3.5 text-sm font-semibold tracking-wide hover:bg-[#11100E] transition-colors flex items-center justify-center gap-2">
                GET STARTED <ArrowRight size={15} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
